import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { wsUrlFor } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles } from "lucide-react";

export default function ChatRoom() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [isSupport, setIsSupport] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    setIsSupport(threadId.startsWith("thr_sup_"));
    api.get(`/chat/${threadId}/messages`).then(({ data }) => setMessages(data));
  }, [threadId]);

  useEffect(() => {
    const ws = new WebSocket(wsUrlFor(threadId));
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        setMessages((prev) => [...prev, msg]);
      } catch { /* ignore malformed frames */ }
    };
    return () => { try { ws.close(); } catch { /* ignore close error */ } };
  }, [threadId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const outgoing = text.trim();
    if (!outgoing || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ text: outgoing }));
    setText("");

    // On a support thread from the traveller, trigger an AI first-response.
    if (isSupport && user?.role === "customer") {
      setAiThinking(true);
      try {
        const { data } = await api.post("/ai/support/reply", { thread_id: threadId, message: outgoing });
        // Append AI reply locally (WS wouldn't have broadcasted since we don't push AI msgs through WS)
        setMessages((prev) => [...prev, {
          message_id: data.message_id,
          thread_id: threadId,
          sender_id: "user_swaanstay_support",
          sender_name: "SwaanStay AI",
          sender_role: "admin",
          text: data.reply + (data.needs_human ? "" : ""),
        }]);
      } catch { /* fall through — human will follow up */ }
      finally { setAiThinking(false); }
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="hw-card overflow-hidden flex flex-col h-[calc(100vh-7rem)]">
          <div className="px-5 py-3 bg-slate-900 text-white flex items-center justify-between">
            <div>
              <div className="hw-label text-emerald-400 flex items-center gap-1">
                {isSupport ? <><Sparkles className="w-3 h-3" /> Support</> : "Chat"}
              </div>
              <div className="hw-head font-bold">{isSupport ? "SwaanStay Support" : "Conversation"}</div>
            </div>
            <span className={`hw-chip border-0 ${connected ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`} data-testid="chat-status">
              {connected ? "● Live" : "○ Offline"}
            </span>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50" data-testid="messages-list">
            {messages.length === 0 && <p className="text-center text-slate-500 hw-label">Start the conversation</p>}
            {messages.map((m, i) => {
              const mine = m.sender_id === user?.user_id;
              const isAI = m.sender_name === "SwaanStay AI" || m.sender_name === "SwaanStay Support";
              return (
                <div key={m.message_id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-4 py-2 rounded-sm ${mine ? "bg-emerald-500 text-slate-900" : isAI ? "bg-blue-50 border border-blue-200 text-slate-900" : "bg-white border border-slate-200 text-slate-900"}`}>
                    <div className="text-xs opacity-70 mb-0.5 flex items-center gap-1">
                      {isAI && !mine && <Sparkles className="w-3 h-3 text-blue-700" />}
                      {m.sender_name} · {m.sender_role}
                    </div>
                    <div className="text-sm break-words whitespace-pre-wrap">{m.text}</div>
                  </div>
                </div>
              );
            })}
            {aiThinking && (
              <div className="flex justify-start" data-testid="ai-thinking">
                <div className="max-w-[75%] px-4 py-2 rounded-sm bg-blue-50 border border-blue-200 text-slate-900">
                  <div className="text-xs opacity-70 mb-0.5 flex items-center gap-1"><Sparkles className="w-3 h-3 text-blue-700" /> SwaanStay AI</div>
                  <div className="text-sm text-slate-600 italic">typing<span className="hw-pulse-dot" />…</div>
                </div>
              </div>
            )}
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
            <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="rounded-sm" />
            <Button type="submit" disabled={!connected || aiThinking} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold" data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
